import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSport } from './SportContext';
import { useRBAC } from './RBACContext';
import { useAuth } from './AuthContext';
import { AZURE } from '../services/api';

/**
 * ActiveContextContext — Club + Entity (athlete/player) selector state.
 *
 * Key design decisions:
 *  - Uses entity_type + entity_id (NOT user_id) because domain entities
 *    (football_player, swimming_athlete) live in separate tables and may
 *    not map to an Entra user_id.
 *  - RBAC enforcement happens server-side (backend returns 403 for
 *    clubs the user cannot access). Frontend trusts the filtered list.
 *  - Persists to localStorage as `vukz_active_context_v1`.
 *  - Reacts to sport changes (clears club/entity when sport changes).
 */

const STORAGE_KEY = 'vukz_active_context_v1';
const LEGACY_KEY = 'sucron_active_context_v1';

const ActiveContextContext = createContext();

export const useActiveContext = () => {
  const ctx = useContext(ActiveContextContext);
  if (!ctx) throw new Error('useActiveContext must be used within ActiveContextProvider');
  return ctx;
};

// ---- helpers ----
const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
    // Migrate from legacy key
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      localStorage.setItem(STORAGE_KEY, legacy);
      localStorage.removeItem(LEGACY_KEY);
      return JSON.parse(legacy);
    }
  } catch { /* ignore */ }
  return null;
};

const persist = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
};

export const ActiveContextProvider = ({ children }) => {
  const { activeSport } = useSport();
  const { clubId: rbacClubId } = useRBAC();
  const { user } = useAuth();

  // Saved state (may be stale if sport changed)
  const saved = useRef(loadSaved());

  // ---- Club state ----
  const [clubId, setClubIdRaw] = useState(() => {
    const s = saved.current;
    return (s && s.sport === activeSport) ? (s.clubId ?? null) : null;
  });
  const [clubName, setClubNameRaw] = useState(() => {
    const s = saved.current;
    return (s && s.sport === activeSport) ? (s.clubName ?? '') : '';
  });

  // ---- Category state (optional — for staff with category scopes) ----
  const [category, setCategoryRaw] = useState(() => {
    const s = saved.current;
    return (s && s.sport === activeSport) ? (s.category ?? null) : null;
  });

  // ---- Entity state (entity_type + entity_id instead of athlete_id) ----
  const [entityId, setEntityIdRaw] = useState(() => {
    const s = saved.current;
    return (s && s.sport === activeSport) ? (s.entityId ?? null) : null;
  });
  const [entityType, setEntityTypeRaw] = useState(() => {
    const s = saved.current;
    return (s && s.sport === activeSport) ? (s.entityType ?? '') : '';
  });
  const [entityName, setEntityNameRaw] = useState(() => {
    const s = saved.current;
    return (s && s.sport === activeSport) ? (s.entityName ?? '') : '';
  });

  // Data lists
  const [clubs, setClubs] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // ------------------------------------------------------------------
  // USER-SWITCH WATCHER (SEC-ACTIVECONTEXT-USER-WATCHER)
  //
  // Consolidates and closes SEC-001-CLUB-DEFAULT #1b (logout leak) and
  // #1c (login-without-logout leak) in one place, at the identity
  // boundary. Reacts to ANY user identity change: login (null->id),
  // logout (id->null), re-login same tab (idA->idB), token refresh,
  // deep-link account switch, future paths we haven't mapped.
  //
  // Design:
  //  - Detects transitions via useRef comparing prev vs current user.id.
  //  - Cleans localStorage FIRST (removeItem is synchronous), then resets
  //    all per-user React state via the setters below.
  //  - Resets hydratedFromRBAC.current so the BOOTSTRAP effect fires for
  //    the new user (fixes the ref-stuck bug that plain localStorage
  //    cleanup on login/logout misses -- because the provider does not
  //    unmount between SPA sessions).
  //
  // State list matches clearAll() (the canonical per-user reset). We do
  // NOT zero setClubs/setLoading* because clubs is per-sport (not per-user)
  // and loading flags belong to their own fetch lifecycle.
  //
  // Persist race window (documented, benign): the persist effect L215+
  // reads clubId from render scope. If setClubIdRaw(null) queues a
  // re-render, persist may fire one more time with the OLD clubId before
  // seeing null. This creates a sub-render window (<1ms) where localStorage
  // briefly holds the previous user's clubId again. The window is not
  // observable by any current code path: loadSaved() only runs in the
  // useState initializer, which only runs on mount, and no mount occurs
  // between the persist write and the next persist write (which grabs
  // null and clears it). Accepted as-is -- documented so future changes
  // to the persist path preserve this assumption.
  //
  // Makes Commits A/B (clearAll ref reset + AuthContext.logout cleanup)
  // defense-in-depth rather than primary -- they still fire and still
  // work, but the watcher is the structural fix.
  const prevUserId = useRef(user?.id ?? null);
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (prevUserId.current !== currentUserId) {
      prevUserId.current = currentUserId;
      // Clean localStorage synchronously first.
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_KEY);
      // Reset RBAC-hydration guard so BOOTSTRAP re-fires for the new user.
      hydratedFromRBAC.current = false;
      // Reset per-user React state (matches clearAll exactly).
      setClubIdRaw(null);
      setClubNameRaw('');
      setCategoryRaw(null);
      setEntityIdRaw(null);
      setEntityTypeRaw('');
      setEntityNameRaw('');
      setSummary(null);
      setAthletes([]);
    }
  }, [user?.id]);

  // ------------------------------------------------------------------
  // BOOTSTRAP: hydrate clubId from RBAC on first RBAC resolution.
  //
  // Fixes SEC-001-CLUB-DEFAULT #1: on fresh session (localStorage empty),
  // clubId starts null and consumers fall back to `activeClubId || 1`,
  // leaking tenant=1 (VUKZ) data to gestors of other clubs.
  //
  // Design:
  //  - Idempotent: ref-guarded so it hydrates ONCE per provider lifetime.
  //  - Never overwrites sport-change resets or explicit setClub() calls
  //    (only fires when clubId is null AND ref hasn't been marked).
  //  - Ref is RESET in clearAll() to allow re-hydration for the next user
  //    logging into the same tab (SPA logout does not unmount the provider).
  const hydratedFromRBAC = useRef(false);
  useEffect(() => {
    if (!hydratedFromRBAC.current && !clubId && rbacClubId) {
      hydratedFromRBAC.current = true;
      setClubIdRaw(rbacClubId);
    }
  }, [rbacClubId, clubId]);

  // ------------------------------------------------------------------
  // Sport change → reset everything
  // ------------------------------------------------------------------
  const prevSport = useRef(activeSport);
  useEffect(() => {
    if (prevSport.current !== activeSport) {
      prevSport.current = activeSport;
      setClubIdRaw(null);
      setClubNameRaw('');
      setCategoryRaw(null);
      setEntityIdRaw(null);
      setEntityTypeRaw('');
      setEntityNameRaw('');
      setAthletes([]);
      setSummary(null);
    }
  }, [activeSport]);

  // ------------------------------------------------------------------
  // Fetch clubs when sport changes (backend does RBAC filtering)
  // ------------------------------------------------------------------
  useEffect(() => {
    // Showcase/marketing routes are fully static — never hit the API
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/showcase/')) {
      setClubs([]);
      return;
    }
    if (!activeSport || !['football', 'swimming', 'athletics'].includes(activeSport)) {
      setClubs([]);
      return;
    }

    let cancelled = false;
    const fetchClubs = async () => {
      setLoadingClubs(true);
      try {
        const { data } = await AZURE.get('/sports/clubs', {
          params: { sport: activeSport, limit: 500 },
        });
        if (!cancelled && data.success) {
          setClubs(data.data || []);
        }
      } catch (err) {
        console.error('[ActiveContext] Failed to load clubs:', err);
        setClubs([]);
      } finally {
        if (!cancelled) setLoadingClubs(false);
      }
    };

    fetchClubs();
    return () => { cancelled = true; };
  }, [activeSport]);

  // ------------------------------------------------------------------
  // Fetch athletes when club changes (backend validates club_id RBAC)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!clubId || !activeSport) {
      setAthletes([]);
      return;
    }

    let cancelled = false;
    const fetchAthletes = async () => {
      setLoadingAthletes(true);
      try {
        const params = { sport: activeSport, club_id: clubId, limit: 200 };
        // Category filter: backend scopes by staff category when present
        if (category) params.category = category;
        const { data } = await AZURE.get('/sports/athletes', { params });
        if (!cancelled && data.success) {
          setAthletes(data.data || []);
        }
      } catch (err) {
        console.error('[ActiveContext] Failed to load athletes:', err);
        setAthletes([]);
      } finally {
        if (!cancelled) setLoadingAthletes(false);
      }
    };

    fetchAthletes();
    return () => { cancelled = true; };
  }, [clubId, activeSport, category]);

  // ------------------------------------------------------------------
  // Fetch summary when context changes
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!clubId || !activeSport) {
      setSummary(null);
      return;
    }

    let cancelled = false;
    const fetchSummary = async () => {
      setLoadingSummary(true);
      try {
        const params = { sport: activeSport, club_id: clubId };
        if (entityId && entityType) {
          params.entity_id = entityId;
          params.entity_type = entityType;
        }
        const { data } = await AZURE.get('/sports/context-summary', { params });
        if (!cancelled && data.success) {
          setSummary(data.data);
        }
      } catch (err) {
        console.error('[ActiveContext] Failed to load summary:', err);
        setSummary(null);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };

    fetchSummary();
    return () => { cancelled = true; };
  }, [clubId, entityId, entityType, activeSport]);

  // ------------------------------------------------------------------
  // Persist to localStorage
  // ------------------------------------------------------------------
  useEffect(() => {
    persist({ sport: activeSport, clubId, clubName, category, entityId, entityType, entityName });
  }, [activeSport, clubId, clubName, category, entityId, entityType, entityName]);

  // ------------------------------------------------------------------
  // Setters
  // ------------------------------------------------------------------
  const setClub = useCallback((id, name) => {
    setClubIdRaw(id);
    setClubNameRaw(name || '');
    // Clear entity + category when club changes
    setCategoryRaw(null);
    setEntityIdRaw(null);
    setEntityTypeRaw('');
    setEntityNameRaw('');
  }, []);

  const setCategory = useCallback((cat) => {
    setCategoryRaw(cat || null);
    // Clear entity when category changes (athlete list will reload)
    setEntityIdRaw(null);
    setEntityTypeRaw('');
    setEntityNameRaw('');
  }, []);

  const setEntity = useCallback((id, type, name) => {
    setEntityIdRaw(id);
    setEntityTypeRaw(type || '');
    setEntityNameRaw(name || '');
  }, []);

  const clearEntity = useCallback(() => {
    setEntityIdRaw(null);
    setEntityTypeRaw('');
    setEntityNameRaw('');
  }, []);

  const clearAll = useCallback(() => {
    // Reset RBAC-hydration guard so the next user logging into the same
    // tab gets their clubId hydrated from THEIR RBAC (not stuck with the
    // previous user's state). Fixes re-login leak in SEC-001-CLUB-DEFAULT.
    hydratedFromRBAC.current = false;
    setClubIdRaw(null);
    setClubNameRaw('');
    setCategoryRaw(null);
    setEntityIdRaw(null);
    setEntityTypeRaw('');
    setEntityNameRaw('');
    setSummary(null);
    setAthletes([]);
  }, []);

  const value = {
    // Current selection
    sport: activeSport,
    clubId,
    clubName,
    category,
    entityId,
    entityType,
    entityName,

    // Data lists for dropdowns
    clubs,
    athletes,
    summary,

    // Loading
    loadingClubs,
    loadingAthletes,
    loadingSummary,

    // Actions
    setClub,
    setCategory,
    setEntity,
    clearEntity,
    clearAll,
  };

  return (
    <ActiveContextContext.Provider value={value}>
      {children}
    </ActiveContextContext.Provider>
  );
};
